// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the shared calendar's store and its sync (calendar-plan.md section 11, decision (c): no live events,
// the client reads since_revision). Refresh points:
// - app start, once the user is known (READY): GET /calendars/@me (404: no calendar, so no dock entry and no banners),
//   then every live event;
// - opening the calendar: @me and since_revision;
// - the window regaining focus, if the last read is over 15 s old: since_revision, and @me if the calendar is open;
// - a gateway reconnect (READY or RESUMED after a drop): since_revision;
// - the user's own writes and a 409: their answer is applied, with no extra read.
// Timers: while open, since_revision every 60 s and @me every 5 min; otherwise since_revision every 5 min while the app
// is in the foreground; nothing while it is hidden. The banners' 30-s tick only re-reads this store. Exclude from
// upstream sync.

import {sortEvents} from '@app/features/calendar/CalendarAgenda';
import type {CalendarApi} from '@app/features/calendar/CalendarApi';
import {CalendarConflictError} from '@app/features/calendar/CalendarApi';
import type {BannerDismissalStorage} from '@app/features/calendar/CalendarBanner';
import {bannerStartKey, dueReminders, pruneDismissals} from '@app/features/calendar/CalendarBanner';
import type {
	Calendar,
	CalendarColorsAnswer,
	CalendarEvent,
	CalendarEventCreateBody,
	CalendarEventPatchBody,
	CalendarEventsAnswer,
} from '@app/features/calendar/CalendarTypes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('Calendar');

export const CALENDAR_OPEN_READ_MS = 60_000;
export const CALENDAR_OPEN_ME_MS = 5 * 60_000;
export const CALENDAR_BACKGROUND_READ_MS = 5 * 60_000;
export const CALENDAR_FOCUS_STALE_MS = 15_000;
export const CALENDAR_BANNER_TICK_MS = 30_000;

/** idle: not started; none: GET @me answered 404; error: @me failed otherwise (retried at the next start). */
export type CalendarStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error';

export interface CalendarStateDeps {
	readonly api: CalendarApi;
	readonly dismissals: BannerDismissalStorage;
	readonly isHidden: () => boolean;
}

export class CalendarState {
	status: CalendarStatus = 'idle';
	calendar: Calendar | null = null;
	readonly events = observable.map<string, CalendarEvent>();
	/** The last revision applied from a list answer (own writes don't move it, so nothing in between is skipped). */
	revision = 0;
	userId: string | null = null;
	isOpen = false;
	/** The banners' clock, moved by the 30-s tick. */
	now = Date.now();
	dismissals: Record<string, string> = {};
	/** The day the calendar opens on ("Open calendar", the mini month). */
	focusDayKey: string | null = null;
	focusSerial = 0;
	private lastReadAt = 0;
	private reading: Promise<void> | null = null;
	private timers: Array<ReturnType<typeof setInterval>> = [];
	private tickTimer: ReturnType<typeof setInterval> | null = null;
	private readonly deps: CalendarStateDeps;

	constructor(deps: CalendarStateDeps) {
		this.deps = deps;
		makeAutoObservable<CalendarState, 'deps' | 'lastReadAt' | 'reading' | 'timers' | 'tickTimer'>(this, {
			deps: false,
			lastReadAt: false,
			reading: false,
			timers: false,
			tickTimer: false,
		});
	}

	/** The dock entry and the banners exist only for a member (GET @me answered a calendar). */
	get isMember(): boolean {
		return this.status === 'ready' && this.calendar != null;
	}

	get timezone(): string {
		return this.calendar?.timezone ?? 'UTC';
	}

	get liveEvents(): Array<CalendarEvent> {
		return sortEvents(this.events.values(), this.timezone);
	}

	get dueReminders(): Array<CalendarEvent> {
		if (!this.isMember) return [];
		return dueReminders(this.events.values(), this.timezone, this.now, this.dismissals);
	}

	/** App start and READY: the first READY for a user starts; a later one is a reconnect. */
	handleReady(userId: string): void {
		if (this.userId === userId && this.status !== 'idle' && this.status !== 'error') {
			this.handleReconnect();
			return;
		}
		void this.start(userId);
	}

	async start(userId: string): Promise<void> {
		if (this.userId !== userId) this.reset();
		if (this.status === 'loading') return;
		this.userId = userId;
		this.status = 'loading';
		this.dismissals = this.deps.dismissals.load(userId);
		if (!(await this.refreshMe())) return;
		await this.readAll();
	}

	reset(): void {
		this.clearTimers();
		this.stopTick();
		this.status = 'idle';
		this.calendar = null;
		this.events.clear();
		this.revision = 0;
		this.userId = null;
		this.dismissals = {};
		this.lastReadAt = 0;
		this.reading = null;
	}

	/** GET @me; false when there is no calendar (404) or it failed. The timers change only with membership. */
	async refreshMe(): Promise<boolean> {
		try {
			const calendar = await this.deps.api.getMyCalendar();
			runInAction(() => {
				const wasMember = this.isMember;
				if (calendar == null) {
					this.status = 'none';
					this.calendar = null;
					this.events.clear();
				} else {
					this.calendar = calendar;
					this.status = 'ready';
				}
				if (this.isMember !== wasMember) this.reschedule();
			});
			return calendar != null;
		} catch (error) {
			logger.warn('Calendar: GET @me failed', error);
			runInAction(() => {
				if (this.calendar == null) this.status = 'error';
			});
			return this.calendar != null;
		}
	}

	/** Every live event (app start). */
	async readAll(): Promise<void> {
		const calendar = this.calendar;
		if (calendar == null) return;
		this.lastReadAt = Date.now();
		try {
			const answer = await this.deps.api.listEvents(calendar.id);
			runInAction(() => {
				this.events.clear();
				this.applyAnswer(answer);
			});
		} catch (error) {
			logger.warn('Calendar: reading the events failed', error);
		}
	}

	/** since_revision; one read at a time. */
	readSince(): Promise<void> {
		const calendar = this.calendar;
		if (!this.isMember || calendar == null) return Promise.resolve();
		if (this.reading != null) return this.reading;
		this.lastReadAt = Date.now();
		const revision = this.revision;
		this.reading = this.deps.api
			.listEventsSince(calendar.id, revision)
			.then((answer) => runInAction(() => this.applyAnswer(answer)))
			.catch((error) => logger.warn('Calendar: since_revision failed', error))
			.finally(() => {
				this.reading = null;
			});
		return this.reading;
	}

	private applyAnswer(answer: CalendarEventsAnswer): void {
		for (const event of answer.events) this.applyEvent(event);
		this.revision = Math.max(this.revision, answer.revision);
	}

	/** An upsert, or a tombstone removed; an older copy never replaces a newer one. */
	private applyEvent(event: CalendarEvent): void {
		const current = this.events.get(event.id);
		if (current != null && current.revision > event.revision) return;
		if (event.deleted) this.events.delete(event.id);
		else this.events.set(event.id, event);
	}

	handleReconnect(): void {
		if (this.isMember) void this.readSince();
		else if (this.status === 'error' && this.userId != null) void this.start(this.userId);
	}

	/** The window or tab became visible, or the window got focus. */
	handleFocus(): void {
		if (this.deps.isHidden()) return;
		this.reschedule();
		if (!this.isMember || Date.now() - this.lastReadAt <= CALENDAR_FOCUS_STALE_MS) return;
		void this.readSince();
		if (this.isOpen) void this.refreshMe();
	}

	handleHidden(): void {
		this.reschedule();
	}

	open(): void {
		this.isOpen = true;
		if (this.isMember) {
			void this.refreshMe();
			void this.readSince();
		} else if ((this.status === 'idle' || this.status === 'error') && this.userId != null) {
			void this.start(this.userId);
		}
		this.reschedule();
	}

	close(): void {
		this.isOpen = false;
		this.reschedule();
	}

	/** "Open calendar": the page opens on that day. */
	focusDay(dayKey: string): void {
		this.focusDayKey = dayKey;
		this.focusSerial += 1;
	}

	/** The page has shown it. */
	clearFocus(): void {
		this.focusDayKey = null;
	}

	private clearTimers(): void {
		for (const timer of this.timers) clearInterval(timer);
		this.timers = [];
	}

	/** The timers for the current state: none while hidden or without a calendar. */
	reschedule(): void {
		this.clearTimers();
		if (this.isMember) this.startTick();
		else this.stopTick();
		if (!this.isMember || this.deps.isHidden()) return;
		if (this.isOpen) {
			this.timers.push(setInterval(() => void this.readSince(), CALENDAR_OPEN_READ_MS));
			this.timers.push(setInterval(() => void this.refreshMe(), CALENDAR_OPEN_ME_MS));
		} else {
			this.timers.push(setInterval(() => void this.readSince(), CALENDAR_BACKGROUND_READ_MS));
		}
	}

	private startTick(): void {
		if (this.tickTimer != null) return;
		this.tickTimer = setInterval(() => this.tick(), CALENDAR_BANNER_TICK_MS);
	}

	private stopTick(): void {
		if (this.tickTimer == null) return;
		clearInterval(this.tickTimer);
		this.tickTimer = null;
	}

	/** The banners' tick: the clock moves and old dismissals go; no request. */
	tick(): void {
		this.now = Date.now();
		if (!this.isMember || this.userId == null) return;
		const pruned = pruneDismissals(this.dismissals, this.events, this.timezone, this.now);
		if (Object.keys(pruned).length !== Object.keys(this.dismissals).length) {
			this.dismissals = pruned;
			this.deps.dismissals.save(this.userId, pruned);
		}
	}

	/** On this device only, for this start; a moved event shows again. */
	dismiss(event: CalendarEvent): void {
		if (this.userId == null) return;
		this.dismissals = {...this.dismissals, [event.id]: bannerStartKey(event)};
		this.deps.dismissals.save(this.userId, this.dismissals);
	}

	private requireCalendar(): Calendar {
		if (this.calendar == null) throw new Error('No calendar');
		return this.calendar;
	}

	async createEvent(body: CalendarEventCreateBody): Promise<CalendarEvent> {
		const event = await this.deps.api.createEvent(this.requireCalendar().id, body);
		runInAction(() => this.applyEvent(event));
		return event;
	}

	/** A 409's event replaces the local copy before the error goes on to the dialog. */
	async updateEvent(eventId: string, body: CalendarEventPatchBody): Promise<CalendarEvent> {
		try {
			const event = await this.deps.api.updateEvent(this.requireCalendar().id, eventId, body);
			runInAction(() => this.applyEvent(event));
			return event;
		} catch (error) {
			if (error instanceof CalendarConflictError) runInAction(() => this.applyEvent(error.event));
			throw error;
		}
	}

	async deleteEvent(eventId: string): Promise<CalendarEvent> {
		const tombstone = await this.deps.api.deleteEvent(this.requireCalendar().id, eventId);
		runInAction(() => this.events.delete(eventId));
		return tombstone;
	}

	async setMyColour(color: string): Promise<CalendarColorsAnswer> {
		const answer = await this.deps.api.setMyColor(this.requireCalendar().id, color);
		runInAction(() => {
			const calendar = this.calendar;
			if (calendar == null) return;
			const byUser = new Map(answer.colors.map((entry) => [entry.user_id, entry.color]));
			this.calendar = {
				...calendar,
				members: calendar.members.map((member) => ({...member, color: byUser.get(member.id) ?? member.color})),
			};
		});
		return answer;
	}
}
